import React, {FC, ReactNode, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {Handler, HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../../../libs/shared/ui/src/styles/mixins';
import {PaletteColorsEnum} from '../../../../../libs/shared/ui/src/styles/paletteModel';
import {UserTheme, useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {StartGuideTaskTypesEnum} from '../../../../models/api/preferencesModel';
import {SettingsRootPagesEnum} from '../../../../models/url/models/types';
import {SettingsAdminHomeUrl} from '../../../../models/url/settingsUrlModel';
import {useHomeShortcutClick} from '../../../../settings/home/settingsHomeShortcutHook';
import {SettingsHomeDestinationsEnum} from '../../../../settings/home/settingsHomeTypes';
import {Banner, BannerTypesEnum} from '../../../common/banners/banner';
import {BannerAction} from '../../../common/banners/bannerAction';
import {BannerCloseAction} from '../../../common/banners/bannerCloseAction';
import {BannerContent} from '../../../common/banners/bannerContent';
import {BannerContentTitle} from '../../../common/banners/bannerContentTitle';
import {BannerFooterActions} from '../../../common/banners/bannerFooterActions';
import {BannerIcon} from '../../../common/banners/bannerIcon';
import {BannerPrioritiesEnum} from '../../../common/banners/bannersContext';
import {taskProgressComplete} from '../../../onboarding/admin/adminOnboardingHelpers';
import {
  inboxTaskTitle,
  teammatesTaskTitle,
  workflowsTaskTitle,
} from '../../../onboarding/admin/tasks/adminOnboardingText';
import {StartGuideProgressBar} from '../../../startGuide/startGuideProgressBar';

/*
 * Styles.
 */

const StyledTaskDescription = styled.div`
  padding-top: 12px;
`;

const StyledTaskLine = styled.div`
  display: grid;
  grid-template-columns: 26px auto;

  font-weight: ${fontWeights.medium};
  font-size: ${fontSizes.small};

  padding-top: 8px;
`;

const StyledTaskTitle = styled.div`
  margin-right: 5px;
  ${ellipsis()}
`;

const StyledTaskCircle = styled.div`
  border-radius: 50%;
  border: 1px solid ${(p) => p.theme.greys.shade50};
  width: 16px;
  height: 16px;
`;

/*
 * Props.
 */

interface AdminSetupBannerProps {
  progress: number;
  shouldAddTeamInbox: boolean;
  shouldAddTeammate: boolean;
  shouldExploreRules: boolean;
  navigateToAdminHome: Handler;
  updateStartGuideTasks: HandlerOf<StartGuideTaskTypesEnum>;
}

/*
 * Style.
 */

const StyledDescriptionDiv = styled.div`
  padding-top: 4px;
  font-size: 12px;
`;

/*
 * Component.
 */

export const AdminSetupBanner: FC<AdminSetupBannerProps> = (props) => {
  const {
    progress,
    shouldAddTeamInbox,
    shouldAddTeammate,
    shouldExploreRules,
    navigateToAdminHome,
    updateStartGuideTasks,
  } = props;
  const [isBannerDismissed, setIsBannerDismissed] = useState(false);
  const theme = useUserTheme();

  const url: SettingsAdminHomeUrl = {rootPage: SettingsRootPagesEnum.ADMIN_HOME};
  const {onShortcutClick} = useHomeShortcutClick(url);

  if (isBannerDismissed) {
    return null;
  }

  const onCloseClick = () => {
    setIsBannerDismissed(true);
    if (progress === taskProgressComplete) {
      updateStartGuideTasks(StartGuideTaskTypesEnum.HELP_CENTER_SUCCESS_DISMISS);
    }
  };

  const {color, priority} = computeBannerProps(theme);

  const action = computeBannerAction({
    shouldAddTeamInbox,
    shouldAddTeammate,
    shouldExploreRules,
    onShortcutClick,
    navigateToAdminHome,
    updateStartGuideTasks,
  });

  return (
    <Banner bannerType={BannerTypesEnum.MULTILINE} color={color} priority={priority}>
      <BannerCloseAction onClick={onCloseClick} />
      <BannerIcon iconName="gearFilledSmall" />
      <BannerContent>
        <BannerContentTitle>Setup {progress}% Completed</BannerContentTitle>
        <StartGuideProgressBar isBanner completionPercentage={progress} />
        <StyledDescriptionDiv>
          {renderDescription({theme, shouldAddTeamInbox, shouldAddTeammate, shouldExploreRules})}
        </StyledDescriptionDiv>
      </BannerContent>
      <BannerFooterActions>
        <BannerAction onClick={action.onClick}>{action.title}</BannerAction>
      </BannerFooterActions>
    </Banner>
  );
};

interface ComputeBannerArgs {
  shouldAddTeamInbox: boolean;
  shouldAddTeammate: boolean;
  shouldExploreRules: boolean;
  onShortcutClick: HandlerOf<SettingsHomeDestinationsEnum>;
  navigateToAdminHome: Handler;
  updateStartGuideTasks: HandlerOf<StartGuideTaskTypesEnum>;
}

interface BannerActionDefinition {
  title: ReactNode;
  isComplete: boolean;
  onClick: Handler;
}

function computeBannerAction({
  shouldAddTeamInbox,
  shouldAddTeammate,
  shouldExploreRules,
  onShortcutClick,
  navigateToAdminHome,
  updateStartGuideTasks,
}: ComputeBannerArgs) {
  const actions: ReadonlyArray<BannerActionDefinition> = [
    {
      title: inboxTaskTitle,
      isComplete: !shouldAddTeamInbox,
      onClick: () => onShortcutClick(SettingsHomeDestinationsEnum.ADD_TEAM_INBOX),
    },
    {
      title: teammatesTaskTitle,
      isComplete: !shouldAddTeammate,
      onClick: () => onShortcutClick(SettingsHomeDestinationsEnum.ADD_TEAMMATES),
    },
    {
      title: workflowsTaskTitle,
      isComplete: !shouldExploreRules,
      onClick: () => {
        onShortcutClick(SettingsHomeDestinationsEnum.ADD_RULE);
        updateStartGuideTasks(StartGuideTaskTypesEnum.ADD_TEAM_RULES);
      },
    },
  ];
  return (
    actions.find((action) => !action.isComplete) ?? {
      title: <Trans>Admin home</Trans>,
      onClick: () => {
        navigateToAdminHome();
      },
    }
  );
}

/*
 * Helpers.
 */

function computeBannerProps(theme: UserTheme) {
  return {
    color: theme.greys.shade90,
    priority: BannerPrioritiesEnum.INFORMATION,
  };
}

interface RenderDescriptionArgs {
  theme: UserTheme;
  shouldAddTeamInbox: boolean;
  shouldAddTeammate: boolean;
  shouldExploreRules: boolean;
}

function renderDescription({
  theme,
  shouldAddTeamInbox,
  shouldAddTeammate,
  shouldExploreRules,
}: RenderDescriptionArgs) {
  return (
    <StyledTaskDescription>
      <StyledTaskLine>
        {renderTaskCircle(theme, !shouldAddTeamInbox)}
        <StyledTaskTitle>{inboxTaskTitle}</StyledTaskTitle>
      </StyledTaskLine>
      <StyledTaskLine>
        {renderTaskCircle(theme, !shouldAddTeammate)}
        <StyledTaskTitle>{teammatesTaskTitle}</StyledTaskTitle>
      </StyledTaskLine>
      <StyledTaskLine>
        {renderTaskCircle(theme, !shouldExploreRules)}
        <StyledTaskTitle>{workflowsTaskTitle}</StyledTaskTitle>
      </StyledTaskLine>
    </StyledTaskDescription>
  );
}

function renderTaskCircle(theme: UserTheme, isComplete: boolean) {
  if (isComplete) {
    return <Icon name="checkmarkCircleSmall" color={theme.palette[PaletteColorsEnum.GREEN].shade40} />;
  }
  return <StyledTaskCircle />;
}
